/**
 * Stage pointer tools — Spawn / Inspect / Rewire.
 * Orbit (3D drag) and wheel dolly always win over tools when the gesture is a drag.
 *
 * Spawn is the default playful action (stays live while playing).
 * Inspect / Rewire auto-pause so targets are clickable at high frame rates.
 */

export type PointerToolId = "spawn" | "inspect" | "rewire";

export type PointerToolDef = {
  id: PointerToolId;
  label: string;
  /** Short transport title */
  summary: string;
  detail: string;
  /** Cursor hint on the stage canvas */
  cursor: string;
  /** True if this tool needs a frozen frame to aim (auto-pauses on use). */
  needsPause: boolean;
};

/** Display + menu order — Spawn first (default). */
export const POINTER_TOOLS: PointerToolDef[] = [
  {
    id: "spawn",
    label: "Spawn (+)",
    summary:
      "Click the stage to add a particle (stays live). Mode chooses F/E coupling.",
    detail:
      "Where: bottom bar → Tool → “Spawn (+)”, then Mode. Click the black stage (not the toolbar). Each click adds one particle under the cursor and bumps n by 1. Playback stays live. Mode picks how the newcomer’s friend/enemy pointers couple (join nearest, local pair, scatter, …) — agents are not typed friend/enemy; each has one outgoing F and one E. Cap ~20k. Separate from the Particles slider (that rebuilds the whole swarm).",
    cursor: "copy",
    needsPause: false,
  },
  {
    id: "inspect",
    label: "Inspect",
    summary: "Click a particle → pause & show friend (blue) / enemy (amber).",
    detail:
      "Teaching tool. A successful click pauses playback so you can read the white halo and blue/amber links while the cloud is still. Space or Play resumes. Click the same particle or empty space to clear. Does not change the math. In 3D, drag still orbits; only a click without drag picks.",
    cursor: "crosshair",
    needsPause: true,
  },
  {
    id: "rewire",
    label: "Rewire",
    summary: "Pause & rewire: source → target (Alt = enemy).",
    detail:
      "Two-step topology edit on a frozen frame: (1) click A — sim pauses and A highlights. (2) click B to set friend[A]→B. Hold Alt/Option on the second click for enemy[A]→B. Empty space or Esc cancels. Play/Space resumes motion. 3D drag still orbits. Use panel “Freeze topology” if you also want the graph to stop auto-rechoosing.",
    cursor: "cell",
    needsPause: true,
  },
];

/** Default stage tool — spawn is the playful primary action. */
export const DEFAULT_POINTER_TOOL: PointerToolId = "spawn";

export function getPointerTool(id: string | null | undefined): PointerToolDef {
  return (
    POINTER_TOOLS.find((t) => t.id === id) ??
    POINTER_TOOLS.find((t) => t.id === DEFAULT_POINTER_TOOL) ??
    POINTER_TOOLS[0]!
  );
}
